import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { TabletCleaningComplete, TabletChecklist } from "@/components/tablet/TabletTaskComplete";

const PAGE = 50;
const FILTERS = [["all", "All"], ["cleaning", "Cleaning"], ["prep", "Prep"], ["checklists", "Checklists"]];
const MOD_LABEL = { cleaning: "Cleaning", prep: "Prep", checklists: "Checklist" };

export const ownerLabel = (t) => (t.ownership === "mine" ? "Assigned to me" : t.ownership === "unassigned" ? "Unassigned" : `Assigned to ${t.assignee_name || "another person"}`);

function TaskCard({ t, i, onTake, onComplete, busy }) {
  const last = t.history?.[t.history.length - 1];
  const tone = t.ownership === "mine" ? "bg-brand text-white" : t.ownership === "unassigned" ? "bg-slate-700 text-slate-200" : "bg-amber-500/20 text-amber-200";
  return (
    <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4" data-testid={`tablet-task-${t.module}-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{MOD_LABEL[t.module]} · <span className={t.due_state === "overdue" ? "text-red-300 font-semibold" : ""}>{t.due_state === "overdue" ? "Overdue · " : ""}{t.due_label}</span>{t.photo_required ? " · photo required" : ""}</p>
          <p className="font-semibold text-white mt-1">{t.title}</p>
          <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`} data-testid={`tablet-task-owner-${i}`}>{ownerLabel(t)}</span>
          {last && <p className="text-xs text-slate-500 mt-2" data-testid={`tablet-task-history-${i}`}>{last.action === "taken_over" ? `Taken over from ${last.from_name || "unassigned"}` : "Taken"} by {last.by_name} · {new Date(last.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>}
        </div>
        <div className="flex gap-2">
          {t.can_take && <button disabled={busy} onClick={() => onTake(t, false)} data-testid={`tablet-task-take-${i}`} className="px-4 py-3 rounded-xl bg-slate-700 font-semibold disabled:opacity-50">Take task</button>}
          {t.can_take_over && <button disabled={busy} onClick={() => onTake(t, true)} data-testid={`tablet-task-takeover-${i}`} className="px-4 py-3 rounded-xl bg-amber-500/25 text-amber-100 font-semibold disabled:opacity-50">Take over</button>}
          {t.can_complete && <button disabled={busy} onClick={() => onComplete(t)} data-testid={`tablet-task-complete-${i}`} className="px-4 py-3 rounded-xl bg-brand text-white font-semibold disabled:opacity-50">{t.module === "checklists" ? "Open checklist" : "Complete"}</button>}
        </div>
      </div>
    </div>
  );
}

// Every open task at this kitchen for the PIN-verified person. Mounted per staff session: locking
// unmounts it, so no task list, photo or answer carries over to the next person.
export function TabletTasks({ client, siteId, initialFilter = "all", onBack }) {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ counts: {}, total: 0, has_more: false });
  const [filter, setFilter] = useState(initialFilter);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const fetchPage = useCallback((offset) => client.get("/tablet/tasks", { params: { offset, limit: PAGE, ...(filter !== "all" ? { module: filter } : {}) } }), [client, filter]);
  const load = useCallback(async () => {
    try { const { data } = await fetchPage(0); setItems(data.items || []); setMeta(data); }
    catch (e) { setItems([]); toast.error(e?.response?.data?.detail || "Could not load tasks"); }
  }, [fetchPage]);
  const more = async () => {
    setBusy(true);
    try { const { data } = await fetchPage(items.length); setItems([...items, ...(data.items || [])]); setMeta(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load more"); }
    finally { setBusy(false); }
  };
  useEffect(() => { setItems(null); load(); }, [load]);

  const take = async (t, takeover) => {
    if (takeover && !window.confirm(`Take over "${t.title}" from ${t.assignee_name || "them"}? It will be recorded against you.`)) return;
    setBusy(true);
    try { await client.post(`/tablet/tasks/${t.module}/${t.id}/take`, { takeover }); toast.success(takeover ? "Task taken over" : "Task is now yours"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not take this task"); }
    finally { setBusy(false); load(); }
  };
  const complete = async (t) => {
    if (t.module !== "prep") { setOpen(t); return; }
    setBusy(true);
    try { await client.post(`/prep/${t.id}/complete`, {}); toast.success("Prep completed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
    finally { setBusy(false); load(); }
  };
  const done = () => { setOpen(null); load(); };
  const shown = items || [];
  const counts = meta.counts || {};
  const sum = (k) => Object.values(counts).reduce((n, c) => n + (c?.[k] || 0), 0);
  const countFor = (k) => (k === "all" ? sum("total") : counts[k]?.total || 0);

  return (
    <div className="p-4 sm:p-6" data-testid="tablet-tasks">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold">Kitchen tasks</h2>
          <p className="text-sm text-slate-400" data-testid="tablet-tasks-counts">{sum("total")} outstanding · {sum("mine")} mine · {sum("unassigned")} unassigned · {sum("overdue")} overdue</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="tablet-tasks-refresh" className="px-3 py-2 rounded-xl bg-slate-800 text-sm flex items-center gap-1"><RefreshCw size={14} /> Refresh</button>
          <button onClick={onBack} data-testid="tablet-tasks-back" className="px-3 py-2 rounded-xl bg-slate-800 text-sm flex items-center gap-1"><ArrowLeft size={14} /> Home</button>
        </div>
      </div>
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`tablet-tasks-filter-${k}`} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${filter === k ? "bg-brand text-white" : "bg-slate-800 text-slate-300"}`}>{l} <span data-testid={`tablet-tasks-count-${k}`}>{countFor(k)}</span></button>
        ))}
      </div>
      {items === null && <div className="flex justify-center py-10 text-brand"><Loader2 className="animate-spin" /></div>}
      {items && shown.length === 0 && <p className="text-slate-400" data-testid="tablet-tasks-empty">No open tasks here right now.</p>}
      <div className="space-y-3">
        {shown.map((t, i) => <TaskCard key={`${t.module}-${t.id}`} t={t} i={i} onTake={take} onComplete={complete} busy={busy} />)}
      </div>
      {meta.has_more && <button disabled={busy} onClick={more} data-testid="tablet-tasks-more" className="w-full mt-4 py-3 rounded-xl bg-slate-800 font-semibold disabled:opacity-50">Show more ({meta.total - shown.length} more)</button>}
      {open?.module === "cleaning" && <TabletCleaningComplete task={open} client={client} siteId={siteId} onClose={() => setOpen(null)} onDone={done} />}
      {open?.module === "checklists" && <TabletChecklist task={open} client={client} onClose={done} onDone={done} />}
    </div>
  );
}
